import { useEffect, type ReactNode } from "react";
import Lenis from "lenis";
import { SCROLL_DURATION, WHEEL_MULTIPLIER, enableNestedSmoothScroll, scrollEasing } from "./nestedSmoothScroll";

interface SmoothScrollProps {
  children: ReactNode;
}

const scrollListeners = new Set<() => void>();

// Scroll-driven animations (e.g. GSAP ScrollTrigger.update) must run inside Lenis's frame,
// otherwise they read the scroll position one frame late and pinned sections jitter.
// Module-level so it works even though child effects run before this component's effect.
export const onSmoothScroll = (listener: () => void) => {
  scrollListeners.add(listener);
  return () => {
    scrollListeners.delete(listener);
  };
};

// Wraps content with Lenis smooth scrolling, works across desktop and mobile/touch devices.
// Lenis is imported statically rather than lazily inside the effect: starting it
// changes how fixed-position elements are laid out, so deferring it pushed that
// change past first paint and showed up as a late layout shift of the cookie
// banner. Initialising during hydration keeps it ahead of anything visible.
const SmoothScroll: React.FC<SmoothScrollProps> = ({ children }) => {
  useEffect(() => {
    const lenis = new Lenis({
      autoRaf: true,
      // Quadratic ease-out, not Lenis's default expo: expo's long sub-pixel tail makes slow scrolls stutter as they come to rest
      duration: SCROLL_DURATION,
      easing: scrollEasing,
      smoothWheel: true,
      wheelMultiplier: WHEEL_MULTIPLIER,
      touchMultiplier: 1.4,
    });

    const offScroll = lenis.on("scroll", () => scrollListeners.forEach((listener) => listener()));
    // Scrollable areas inside components get the same easing instead of native wheel jumps.
    // It also hands Safari trackpad gestures back to native scrolling (see nestedSmoothScroll.ts).
    const disableNested = enableNestedSmoothScroll(() => {
      if (lenis.isScrolling === "smooth") lenis.scrollTo(lenis.animatedScroll, { immediate: true });
    });

    return () => {
      offScroll();
      disableNested();
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
};

export default SmoothScroll;
